import { useId, useRef, useState } from 'react';
import { X, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { useModalFocus } from '../hooks/useModalFocus';

export function ChangePasswordDrawer({ isOpen, onClose }) {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const passwordError = (pw) => {
    if (!pw) return 'New password is required';
    if (pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[a-z]/.test(pw)) return 'Add at least one lowercase letter';
    if (!/[A-Z]/.test(pw)) return 'Add at least one uppercase letter';
    if (!/[0-9]/.test(pw)) return 'Add at least one number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) return 'Add at least one symbol';
    return '';
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!currentPassword) nextErrors.currentPassword = 'Current password is required';

    const pwErr = passwordError(newPassword);
    if (pwErr) nextErrors.newPassword = pwErr;
    if (currentPassword && newPassword && currentPassword === newPassword) {
      nextErrors.newPassword = 'New password must be different from current password';
    }
    if (!confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = 'New passwords do not match';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (!user || !user.email) {
      setErrors({
        currentPassword: 'Unable to verify your account. Please sign in again.',
      });
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccessMessage('Password updated successfully');
      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setErrors({ currentPassword: 'Current password is incorrect' });
      } else if (code === 'auth/too-many-requests') {
        setErrors({ currentPassword: 'Too many attempts. Please try again shortly.' });
      } else if (code === 'auth/requires-recent-login') {
        setErrors({ currentPassword: 'Please sign out and sign in again, then retry.' });
      } else if (code === 'auth/weak-password') {
        setErrors({ newPassword: 'Please choose a stronger password' });
      } else {
        setErrors({ confirmPassword: 'Could not update password. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form on close
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setIsSubmitting(false);
    setSuccessMessage('');
    setErrors({});
    onClose();
  };

  const panelRef = useRef(null);
  const titleId = useId();
  useModalFocus({ isOpen, panelRef, onClose: handleClose, canClose: !isSubmitting });

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div 
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 transition-transform duration-300 ease-out focus:outline-none ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="max-w-md mx-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          <div className="px-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 id={titleId} className="text-xl">Change Password</h2>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                className="w-11 h-11 -mr-2 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              {successMessage ? (
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]">
                  <CheckCircle2 className="w-4 h-4 text-[#1F5D38]" />
                  <span>{successMessage}</span>
                </div>
              ) : null}

              {/* Current Password */}
              <div className="mb-4">
                <label htmlFor="currentPassword" className="block text-foreground mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      clearFieldError('currentPassword');
                    }}
                    placeholder="Enter current password"
                    className={`w-full px-4 py-3 bg-input rounded-xl border-0 pr-12 ${
                      errors.currentPassword ? 'ring-2 ring-[#B85A5A]' : ''
                    }`}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isSubmitting}
                  >
                    {showCurrent ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                  </button>
                </div>
                {errors.currentPassword ? (
                  <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: '#B85A5A' }}>
                    <AlertCircle className="w-4 h-4" color="#B85A5A" style={{ color: '#B85A5A', stroke: '#B85A5A' }} />
                    <span style={{ color: '#B85A5A' }}>{errors.currentPassword}</span>
                  </div>
                ) : null}
              </div>

              {/* New Password */}
              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-foreground mb-2">
                  New Password
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Must be at least 8 characters and include:
                  <br />
                   - At least one lowercase letter
                  <br />
                   - At least one uppercase letter
                  <br />
                   - At least one number
                  <br />
                   - At least one symbol (e.g., !@#$%^&*)
                    </p>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      clearFieldError('newPassword');
                      clearFieldError('confirmPassword');
                    }}
                    placeholder="Enter new password"
                    className={`w-full px-4 py-3 bg-input rounded-xl border-0 pr-12 ${
                      errors.newPassword ? 'ring-2 ring-[#B85A5A]' : ''
                    }`}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    aria-label={showNew ? 'Hide new password' : 'Show new password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isSubmitting}
                  >
                    {showNew ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                  </button>
                </div>
                
                {errors.newPassword ? (
                  <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: '#B85A5A' }}>
                    <AlertCircle className="w-4 h-4" color="#B85A5A" style={{ color: '#B85A5A', stroke: '#B85A5A' }} />
                    <span style={{ color: '#B85A5A' }}>{errors.newPassword}</span>
                  </div>
                ) : null}
              </div>

              {/* Confirm New Password */}
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-foreground mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      clearFieldError('confirmPassword');
                    }}
                    placeholder="Re-enter new password"
                    className={`w-full px-4 py-3 bg-input rounded-xl border-0 pr-12 ${
                      errors.confirmPassword ? 'ring-2 ring-[#B85A5A]' : ''
                    }`}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    aria-label={showConfirm ? 'Hide new password confirmation' : 'Show new password confirmation'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isSubmitting}
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                  </button>
                </div>
                {errors.confirmPassword ? (
                  <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: '#B85A5A' }}>
                    <AlertCircle className="w-4 h-4" color="#B85A5A" style={{ color: '#B85A5A', stroke: '#B85A5A' }} />
                    <span style={{ color: '#B85A5A' }}>{errors.confirmPassword}</span>
                  </div>
                ) : null}
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-3 bg-white text-[#5F6B3F] font-medium rounded-full border-2 border-[#5F6B3F] hover:bg-[#F0F3EC] transition-colors disabled:opacity-60"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors disabled:opacity-60"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
